import { pppCountryToStripe, stripeCountryToPPP } from "./ppp.js";

export function rawMultiplier(pppRows, countryAlpha2) {
  const code = stripeCountryToPPP(String(countryAlpha2 || "").toUpperCase());
  if (!code) return null;
  const row = (pppRows || []).find((entry) => entry.code === code);
  const multiplier = Number(row?.multiplier);
  return multiplier > 0 ? multiplier : null;
}

/**
 * Net price in euro cents for one buyer country.
 * PPP uses the raw World Bank ratio against the creator's payout country.
 * The creator's floor is the only lower bound. The reference country keeps
 * the price they typed.
 */
export function pluginPriceQuote(plugin, buyerCountry, pppRows) {
  const priceCents = Math.round(Number(plugin.priceCents) || 0);
  const floorCents = Math.round(Number(plugin.floorCents) || 0);
  const buyer = String(buyerCountry || "").toUpperCase();
  if (plugin.mode !== "ppp") {
    return { cents: priceCents, uncappedCents: priceCents, floorApplied: false };
  }
  const reference = String(plugin.referenceCountry || "").toUpperCase();
  const referenceRaw = rawMultiplier(pppRows, reference);
  const buyerRaw = rawMultiplier(pppRows, buyer);
  if (!referenceRaw || !buyerRaw || buyer === reference) {
    return { cents: priceCents, uncappedCents: priceCents, floorApplied: false };
  }
  const uncappedCents = Math.round((priceCents * (buyerRaw / referenceRaw)) / 100) * 100;
  const cents = Math.max(floorCents, uncappedCents);
  return {
    cents,
    uncappedCents,
    floorApplied: cents > uncappedCents,
  };
}

export function pluginPriceForCountry(plugin, buyerCountry, pppRows) {
  return pluginPriceQuote(plugin, buyerCountry, pppRows).cents;
}

export function pluginPriceRange(plugin, pppRows) {
  if (plugin.mode !== "ppp") return null;
  let min = null;
  let max = null;
  for (const row of pppRows || []) {
    const country = pppCountryToStripe(row.code);
    if (!country) continue;
    const quote = pluginPriceQuote(plugin, country, pppRows);
    const entry = { country, name: row.name || country, ...quote };
    // Several countries can share the charged minimum once the floor is
    // applied. Name the lowest PPP price, not whichever of those rows
    // appears first in the World Bank file.
    if (!min || entry.uncappedCents < min.uncappedCents) min = entry;
    if (!max || entry.uncappedCents > max.uncappedCents) max = entry;
  }
  return { min, max };
}

export function formatPluginPriceRange(range) {
  if (!range?.min || !range?.max) return "Country prices are not available yet.";
  const euros = (cents) => `€${(Number(cents) / 100).toFixed(2)}`;
  const lowest = range.min.floorApplied
    ? `${range.min.name} ${euros(range.min.cents)} (PPP price ${euros(range.min.uncappedCents)}, your minimum is ${euros(range.min.cents)})`
    : `${range.min.name} ${euros(range.min.cents)}`;
  return `Lowest: ${lowest} · Highest: ${range.max.name} ${euros(range.max.cents)}`;
}

/**
 * The billing address chooses the country price. VAT is calculated for that
 * same address. A card issued somewhere else does not raise the price, or
 * the VAT would be added on top of a second country's uplift.
 */
export function priceCountry(billingCountry, cardCountry, _pppRows) {
  const billing = String(billingCountry || "").toUpperCase();
  const card = String(cardCountry || "").toUpperCase();
  if (!billing) return { country: null, mismatch: false };
  return { country: billing, mismatch: Boolean(card && card !== billing) };
}

export function commercialPriceInput(body) {
  const pricingMode = body?.pricingMode === "ppp" ? "ppp" : "flat";
  const priceCents = Math.round(Number(body?.priceCents));
  if (!Number.isFinite(priceCents)) return { error: "Enter a price in euros." };
  if (pricingMode === "flat") {
    if (priceCents < 50) return { error: "The price has to be at least €0.50." };
    return { pricingMode, priceCents, floorCents: null };
  }
  const floorCents = Math.round(Number(body?.floorCents));
  if (priceCents < 100) return { error: "The price has to be at least €1." };
  if (!Number.isFinite(floorCents) || floorCents < 100 || floorCents > priceCents) {
    return {
      error: "The lowest price has to be at least €1 and no more than the main price.",
    };
  }
  return { pricingMode, priceCents, floorCents };
}
